import { Settings2, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { showAlert } from "../../../../store/alertSlice";
import { Button } from "../../../../components/tailwind/button";
import { Label } from "../../../../components/tailwind/label";
import { Input } from "../../../../components/tailwind/input";
import { resolveNodeConfigSchema, schemaToFields } from "../../utils/workflowSchemaUtils";
import {
  getCanvasNodePreviewText,
  PALETTE
} from "../../utils/workflowPaletteConstants";
import { workflowTheme } from "../../utils/workflowBuilderTheme";
import {
  applyAutoSaveToContext,
  applyCollectInputExpectedTypeChange,
  deriveStoreAsFromLabel,
  getPaletteDisplayName,
  getWorkflowNodeDescription,
  mergeNodeConfigPatch,
  normalizeNodeConfigForPalette,
  usesWorkflowPicker,
  shouldHideInspectorField,
  shouldShowSaveToContextUi
} from "../../utils/workflowInspectorUx";

import { ConfigSchemaForm, NODE_LEVEL_SCHEMA_FIELDS } from "./ConfigSchemaForm";
import { ConditionNodeFields } from "./ConditionNodeFields";
import { SaveToContextFields } from "./SaveToContextFields";
import { inspectorInputClass, inspectorSelectClass } from "./RightInspectorStyles";

const MESSAGE_OPTIONS_BY_PALETTE = {
  "whatsapp.notify": [
    { value: "text", label: "Text" },
    { value: "image", label: "Image" },
    { value: "video", label: "Video" },
    { value: "document", label: "Document" },
    { value: "template", label: "Template" }
  ],
  "whatsapp.await": [
    { value: "text", label: "Text" },
    { value: "buttons", label: "Buttons" },
    { value: "list", label: "List" },
    { value: "image", label: "Image" },
    { value: "video", label: "Video" },
    { value: "document", label: "Document" },
    { value: "template", label: "Template" }
  ]
};

export function RightInspectorCatalog({
  selectedNode,
  onUpdateNode,
  onDeleteNode,
  onClose,
  onPendingMediaChange,
  onRegisterPendingUploadNode,
  mediaFlushSignal = 0,
  uploadSilent = true,
  currentWorkflowId
}) {
  const dispatch = useDispatch();
  const catalogData = useSelector((state) => state.workflowCatalog?.data);
  const { workflows = [], loading: workflowsLoading } = useSelector((state) => state.workflowList || {});
  const actions = catalogData?.actions ?? [];
  const conditions = catalogData?.conditions ?? [];

  const handlePendingMediaChange = useCallback((pending) => {
    onPendingMediaChange?.(pending);
  }, [onPendingMediaChange]);

  useEffect(() => {
    if (!selectedNode?.data) return;

    const { config = {}, paletteType, label } = selectedNode.data;
    const normalized = normalizeNodeConfigForPalette(config, paletteType, label);
    const expectedType = normalized.message?.type;
    if (!expectedType || config.message?.type === expectedType) return;

    onUpdateNode(
      selectedNode.id,
      (prevData) => applyAutoSaveToContext({
        ...prevData,
        config: normalized,
      })
    );
  }, [selectedNode?.id, selectedNode?.data?.paletteType, onUpdateNode]);

  const configSchema = useMemo(() => {
    if (!selectedNode) return {};
    return resolveNodeConfigSchema(
      catalogData,
      selectedNode.data.workflowType,
      selectedNode.data.paletteType
    );
  }, [catalogData, selectedNode]);

  const configFieldCount = useMemo(() => {
    const paletteType = selectedNode?.data?.paletteType;
    return schemaToFields(configSchema).filter(
      (field) =>
        !NODE_LEVEL_SCHEMA_FIELDS.has(field.name) && !shouldHideInspectorField(field.name, paletteType)
    ).length;
  }, [configSchema, selectedNode?.data?.paletteType]);

  if (!selectedNode) {
    return (
      <aside className="absolute right-0 top-0 bottom-0 z-20 w-[min(100%,20rem)] xl:relative xl:z-auto xl:w-80 bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-800 flex flex-col h-full justify-center items-center p-6 text-center shrink-0 shadow-xl xl:shadow-none">
        <div className="w-12 h-12 bg-gray-50 dark:bg-slate-950 rounded-full flex items-center justify-center mb-4 border border-gray-100 dark:border-slate-800">
          <Settings2 className="w-6 h-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Select a node to configure</p>
        <p className="text-xs text-gray-400 mt-2">Click any node on the canvas to see its settings here.</p>
      </aside>
    );
  }

  const data = selectedNode.data;
  const config = data.config || {};
  const selectedAction = actions.find((action) => action.type === config.action);
  const catalog = { actions, conditions };

  const currentMessageType = config.message?.type || "text";

  const handleMessageTypeChange = (newMessageType) => {
    const prevMessage = config.message || {};
    const hasUploadedFile = Boolean(prevMessage.url);
    const hasPendingFile = Boolean(prevMessage.pendingFile);

    if (hasUploadedFile || hasPendingFile) {
      dispatch(
        showAlert({
          message: "Please remove the selected media file before changing the message type.",
          type: "warning",
        })
      );
      return;
    }

    const type = newMessageType || "text";
    const newMessage = {
      ...prevMessage,
      type
    };

    if (type === "text" || type === "buttons" || type === "list") {
      if (newMessage.text === undefined) newMessage.text = "";
    }
    if (type === "buttons") {
      if (!Array.isArray(newMessage.options)) newMessage.options = [];
    }
    if (type === "list") {
      if (!Array.isArray(newMessage.items)) newMessage.items = [];
    }
    if (type === "image" || type === "video" || type === "document") {
      if (newMessage.url === undefined) newMessage.url = "";
      if (newMessage.caption === undefined) newMessage.caption = "";
    }
    if (type === "document") {
      if (newMessage.filename === undefined) newMessage.filename = "";
    }

    const newConfig = {
      ...config,
      message: newMessage
    };

    onUpdateNode(selectedNode.id, (prevData) => {
      const nextData = {
        ...prevData,
        config: newConfig,
        hasError: false
      };

      const preview = getCanvasNodePreviewText(nextData);
      if (preview) nextData.description = preview;

      return applyAutoSaveToContext(nextData);
    });
  };

  const updateConfig = (patch) => {
    onUpdateNode(selectedNode.id, (prevData) => {
      let mergedConfig = mergeNodeConfigPatch(prevData.config || {}, patch);

      if (prevData.paletteType === PALETTE.COLLECT_INPUT && patch.expectedType !== undefined) {
        mergedConfig = applyCollectInputExpectedTypeChange(
          mergedConfig,
          prevData.config?.expectedType || "text",
          patch.expectedType
        );
      }

      const normalizedConfig = normalizeNodeConfigForPalette(
        mergedConfig,
        prevData.paletteType,
        prevData.label
      );
      const nextData = {
        ...prevData,
        config: normalizedConfig,
        hasError: false
      };

      if (
        usesWorkflowPicker(prevData.paletteType) &&
        patch.workflowId &&
        patch.workflowId !== prevData.config?.workflowId
      ) {
        const target = workflows.find((workflow) => workflow.workflowId === patch.workflowId);
        nextData.description = getWorkflowNodeDescription(prevData.paletteType, target?.name);
      } else {
        const preview = getCanvasNodePreviewText(nextData);
        if (preview) nextData.description = preview;
      }

      return applyAutoSaveToContext(nextData);
    });
  };

  const handleLabelChange = (nextLabel) => {
    onUpdateNode(selectedNode.id, (prevData) => {
      const prevDerivedStoreAs = deriveStoreAsFromLabel(prevData.label);
      const currentStoreAs = prevData.config?.storeAs;
      const syncStoreAs =
        prevData.paletteType === PALETTE.COLLECT_INPUT &&
        (currentStoreAs == null || currentStoreAs === prevDerivedStoreAs);

      const mergedConfig = mergeNodeConfigPatch(
        prevData.config || {},
        syncStoreAs ? { storeAs: deriveStoreAsFromLabel(nextLabel) } : {}
      );
      const normalizedConfig = normalizeNodeConfigForPalette(
        mergedConfig,
        prevData.paletteType,
        nextLabel
      );
      const nextData = {
        ...prevData,
        label: nextLabel,
        config: normalizedConfig,
        hasError: false
      };
      const preview = getCanvasNodePreviewText(nextData);
      if (preview) nextData.description = preview;

      return applyAutoSaveToContext(nextData);
    });
  };

  const stepTypeLabel = getPaletteDisplayName(data.paletteType, catalogData);

  return (
    <aside className={`absolute right-0 top-0 bottom-0 z-20 w-[min(100%,20rem)] xl:relative xl:z-auto xl:w-80 ${workflowTheme.surface} border-l ${workflowTheme.border} flex flex-col h-full shrink-0 shadow-xl xl:shadow-none animate-slide-in-right`}>
      <div className={`p-4 border-b ${workflowTheme.borderSubtle} flex justify-between items-start gap-2 ${workflowTheme.surfaceMuted}`}>
        <div className="min-w-0 flex-1">
          <h3 className={`font-semibold ${workflowTheme.textPrimary} text-sm truncate`}>{data.label}</h3>
          {stepTypeLabel && (
            <p className={`text-xs ${workflowTheme.textSecondary} mt-0.5 truncate`}>{stepTypeLabel}</p>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800"
            onClick={onClose}
            aria-label="Close panel"
          >
            <X className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40"
            onClick={() => onDeleteNode(selectedNode.id)}
            aria-label="Delete node"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="p-4 flex-1 overflow-y-auto space-y-6">
        <div className="space-y-2">
          <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Step name</Label>
          <Input
            value={data.label}
            onChange={(e) => handleLabelChange(e.target.value)}
            className={inspectorInputClass}
          />
        </div>

        {MESSAGE_OPTIONS_BY_PALETTE[data.paletteType] && (
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Message type</Label>
            <select
              value={currentMessageType}
              onChange={(e) => handleMessageTypeChange(e.target.value)}
              className={inspectorSelectClass}
            >
              {MESSAGE_OPTIONS_BY_PALETTE[data.paletteType].map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {configFieldCount === 0 &&
          data.paletteType !== PALETTE.CONDITION &&
          data.paletteType !== PALETTE.END && (
            <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded p-2">
              No configuration fields loaded. Check that GET_CATALOG returned a schema for this node type.
            </p>
          )}

        <ConfigSchemaForm
          schema={configSchema}
          config={config}
          onConfigChange={updateConfig}
          catalog={catalog}
          selectedAction={selectedAction}
          paletteType={data.paletteType}
          workflowType={data.workflowType}
          workflows={workflows}
          workflowsLoading={workflowsLoading}
          currentWorkflowId={currentWorkflowId}
          saveSignal={mediaFlushSignal}
          uploadSilent={uploadSilent}
          nodeId={selectedNode.id}
          onPendingMediaChange={handlePendingMediaChange}
          onRegisterPendingUploadNode={onRegisterPendingUploadNode}
          showErrors={data.hasError}
        />

        {data.paletteType === PALETTE.CONDITION && (
          <ConditionNodeFields
            data={data}
            conditionsCatalog={conditions}
            onUpdate={(nextData) => onUpdateNode(selectedNode.id, nextData)}
          />
        )}

        {shouldShowSaveToContextUi(data.paletteType, data.workflowType, data.config) && (
          <SaveToContextFields
            data={data}
            paletteType={data.paletteType}
            workflowType={data.workflowType}
            onUpdate={(nextData) => onUpdateNode(selectedNode.id, nextData)}
          />
        )}
      </div>
    </aside>
  );
}
